import {useEffect, useRef} from "react";

import {RegistrationPreferencesModal} from "../../../auth/components/registration_flow/RegistrationPreferencesModal";
import {useHomeMatchBasicPreferencesFlow} from "../../hooks/use_home_match_basic_preferences_flow";
import {
    trackMultileadPreferencesChange,
    trackMultileadPreferencesClose,
    trackMultileadPreferencesSaved,
    trackMultileadPreferencesView
} from "../../tracking/amplitude/track_multilead_preferences_events";
import {IHomeMatchBasicPreferencesFlowProps} from "../../types/HomeMatchBasicPreferencesFlowTypes";
import {getHomeMatchBasicSurveyCopy} from "../../utils/home_match_basic_preferences_flow_utils";
import {HomeMatchBasicPreferencesFlowLoadingModal} from "./HomeMatchBasicPreferencesFlowLoadingModal";
import {HomeMatchBasicPreferencesFlowPhoneUpdateModal} from "./HomeMatchBasicPreferencesFlowPhoneUpdateModal";
import {HomeMatchBasicPreferencesFlowPhoneVerificationModal} from "./HomeMatchBasicPreferencesFlowPhoneVerificationModal";
import {HomeMatchBasicPreferencesFlowResignationModal} from "./HomeMatchBasicPreferencesFlowResignationModal";
import {HomeMatchBasicPreferencesFlowSummaryModal} from "./HomeMatchBasicPreferencesFlowSummaryModal";

export const HomeMatchBasicPreferencesFlow = (props: IHomeMatchBasicPreferencesFlowProps) => {
    const {
        isOpen,
        source,
        defaultHomeMatchBasicConsent,
        initialStep = "auto",
        propertyId,
        offerId,
        originalApplicationResponse,
        trackingContext,
        amplitudeTrackingContext,
        overlayVariant,
        onComplete
    } = props;
    const amplitudeViewTrackedRef = useRef(false);
    const amplitudeSaveTrackedRef = useRef(false);
    const flow = useHomeMatchBasicPreferencesFlow({
        isOpen,
        source,
        defaultHomeMatchBasicConsent,
        initialStep,
        propertyId,
        offerId,
        originalApplicationResponse,
        onComplete
    });

    useEffect(() => {
        if (!isOpen) {
            amplitudeViewTrackedRef.current = false;
            amplitudeSaveTrackedRef.current = false;
            return;
        }

        if (flow.step === "summary" && !flow.isLoading && amplitudeTrackingContext && !amplitudeViewTrackedRef.current) {
            amplitudeViewTrackedRef.current = true;
            trackMultileadPreferencesView({
                context: amplitudeTrackingContext,
                values: flow.summaryValues,
                autoMatchEnabled: flow.homeMatchBasicConsent,
                regionDetails: flow.regionDetails
            });
        }
    }, [amplitudeTrackingContext, flow.homeMatchBasicConsent, flow.isLoading, flow.regionDetails, flow.step, flow.summaryValues, isOpen]);

    const handleEditPreferences = () => {
        if (amplitudeTrackingContext) {
            trackMultileadPreferencesChange(amplitudeTrackingContext);
        }

        flow.handleEditPreferences();
    };

    const handleSummaryConfirm = () => {
        if (amplitudeTrackingContext && !amplitudeSaveTrackedRef.current) {
            amplitudeSaveTrackedRef.current = true;
            trackMultileadPreferencesSaved({
                context: amplitudeTrackingContext,
                values: flow.summaryValues,
                autoMatchEnabled: flow.homeMatchBasicConsent,
                regionDetails: flow.regionDetails
            });
        }

        flow.handleSummaryConfirm();
    };

    const handleSummaryClose = () => {
        if (amplitudeTrackingContext && !amplitudeSaveTrackedRef.current) {
            trackMultileadPreferencesClose({
                context: amplitudeTrackingContext,
                values: flow.summaryValues,
                autoMatchEnabled: flow.homeMatchBasicConsent,
                regionDetails: flow.regionDetails
            });
        }

        flow.handleSummaryClose();
    };

    if (!isOpen) {
        return null;
    }

    if (flow.step === "completed") {
        return null;
    }

    if (flow.step === "resolving" || flow.isLoading || flow.isHomeMatchConfigurationLoading) {
        return <HomeMatchBasicPreferencesFlowLoadingModal isOpen={isOpen} overlayVariant={overlayVariant} />;
    }

    if (flow.step === "survey") {
        const surveyCopy = getHomeMatchBasicSurveyCopy(flow.isEditingSurvey);

        return (
            <RegistrationPreferencesModal
                isOpen={isOpen}
                isPropertyApplication={Boolean(propertyId)}
                propertyId={propertyId}
                offerId={offerId || undefined}
                application={originalApplicationResponse}
                surveySource={flow.surveySource}
                title={surveyCopy.title}
                description={surveyCopy.description}
                submitButtonText={flow.isEditingSurvey ? "ZAPISZ" : "ZAPISZ SWOJE PREFERENCJE"}
                closeButtonText={surveyCopy.closeButtonText}
                onCancel={flow.isEditingSurvey ? flow.handleSurveyCancel : undefined}
                overrideDefaultValues={flow.isEditingSurvey ? flow.summaryValues : undefined}
                onSuccess={flow.handleSurveySuccess}
                showPriceHeader={!flow.isEditingSurvey}
                showCreditPlan={!flow.isEditingSurvey}
                layoutVariant={flow.isEditingSurvey ? "homeMatchBasicEdit" : "homeMatchBasicSurvey"}
                gtmEventCategory={flow.isEditingSurvey ? trackingContext?.eventCategory : undefined}
                gtmCustomLabel={flow.isEditingSurvey ? (trackingContext?.sourceModal ?? trackingContext?.eventName) : undefined}
                overlayVariant={overlayVariant}
            />
        );
    }

    if (flow.step === "resignation") {
        return (
            <HomeMatchBasicPreferencesFlowResignationModal
                isOpen={isOpen}
                isSaving={flow.isSaving}
                onDisable={() => flow.saveHomeMatchDecision(false, "stayModal")}
                onKeepEnabled={flow.handleKeepEnabled}
                onClose={flow.handleResignationClose}
                trackingContext={trackingContext}
                overlayVariant={overlayVariant}
            />
        );
    }

    if (flow.step === "phoneVerification") {
        return (
            <HomeMatchBasicPreferencesFlowPhoneVerificationModal
                isOpen={isOpen}
                onClose={flow.handlePhoneVerificationClose}
                onSuccess={flow.handlePhoneVerificationSuccess}
                onChangeNumber={flow.handlePhoneVerificationChangeNumber}
                overlayVariant={overlayVariant}
            />
        );
    }

    if (flow.step === "phoneUpdate") {
        return (
            <HomeMatchBasicPreferencesFlowPhoneUpdateModal
                isOpen={isOpen}
                onClose={flow.handlePhoneUpdateClose}
                onSuccess={flow.handlePhoneUpdateSuccess}
                overlayVariant={overlayVariant}
            />
        );
    }

    return (
        <HomeMatchBasicPreferencesFlowSummaryModal
            isOpen={isOpen}
            source={source}
            values={flow.summaryValues}
            regionDetails={flow.regionDetails}
            homeMatchBasicConsent={flow.homeMatchBasicConsent}
            isSaving={flow.isSaving}
            onHomeMatchBasicConsentChange={flow.setHomeMatchBasicConsent}
            onEditPreferences={handleEditPreferences}
            onConfirm={handleSummaryConfirm}
            onClose={handleSummaryClose}
            trackingContext={trackingContext}
            overlayVariant={overlayVariant}
        />
    );
};
import {useEffect, useRef} from "react";

import {RegistrationPreferencesModal} from "../../../auth/components/registration_flow/RegistrationPreferencesModal";
import {useHomeMatchBasicPreferencesFlow} from "../../hooks/use_home_match_basic_preferences_flow";
import {
    trackMultileadPreferencesChange,
    trackMultileadPreferencesClose,
    trackMultileadPreferencesSaved,
    trackMultileadPreferencesView
} from "../../tracking/amplitude/track_multilead_preferences_events";
import {IHomeMatchBasicPreferencesFlowProps} from "../../types/HomeMatchBasicPreferencesFlowTypes";
import {getHomeMatchBasicSurveyCopy} from "../../utils/home_match_basic_preferences_flow_utils";
import {HomeMatchBasicPreferencesFlowLoadingModal} from "./HomeMatchBasicPreferencesFlowLoadingModal";
import {HomeMatchBasicPreferencesFlowPhoneUpdateModal} from "./HomeMatchBasicPreferencesFlowPhoneUpdateModal";
import {HomeMatchBasicPreferencesFlowPhoneVerificationModal} from "./HomeMatchBasicPreferencesFlowPhoneVerificationModal";
import {HomeMatchBasicPreferencesFlowResignationModal} from "./HomeMatchBasicPreferencesFlowResignationModal";
import {HomeMatchBasicPreferencesFlowSummaryModal} from "./HomeMatchBasicPreferencesFlowSummaryModal";

export const HomeMatchBasicPreferencesFlow = (props: IHomeMatchBasicPreferencesFlowProps) => {
    const {
        isOpen,
        source,
        defaultHomeMatchBasicConsent,
        initialStep = "auto",
        propertyId,
        offerId,
        originalApplicationResponse,
        trackingContext,
        amplitudeTrackingContext,
        overlayVariant,
        onComplete
    } = props;
    const amplitudeViewTrackedRef = useRef(false);
    const amplitudeSaveTrackedRef = useRef(false);
    const flow = useHomeMatchBasicPreferencesFlow({
        isOpen,
        source,
        defaultHomeMatchBasicConsent,
        initialStep,
        propertyId,
        offerId,
        originalApplicationResponse,
        onComplete
    });

    useEffect(() => {
        if (!isOpen) {
            amplitudeViewTrackedRef.current = false;
            amplitudeSaveTrackedRef.current = false;
            return;
        }

        if (flow.step === "summary" && !flow.isLoading && amplitudeTrackingContext && !amplitudeViewTrackedRef.current) {
            amplitudeViewTrackedRef.current = true;
            trackMultileadPreferencesView({
                context: amplitudeTrackingContext,
                values: flow.summaryValues,
                autoMatchEnabled: flow.homeMatchBasicConsent,
                regionDetails: flow.regionDetails
            });
        }
    }, [amplitudeTrackingContext, flow.homeMatchBasicConsent, flow.isLoading, flow.regionDetails, flow.step, flow.summaryValues, isOpen]);

    const handleEditPreferences = () => {
        if (amplitudeTrackingContext) {
            trackMultileadPreferencesChange(amplitudeTrackingContext);
        }

        flow.handleEditPreferences();
    };

    const handleSummaryConfirm = () => {
        if (amplitudeTrackingContext && !amplitudeSaveTrackedRef.current) {
            amplitudeSaveTrackedRef.current = true;
            trackMultileadPreferencesSaved({
                context: amplitudeTrackingContext,
                values: flow.summaryValues,
                autoMatchEnabled: flow.homeMatchBasicConsent,
                regionDetails: flow.regionDetails
            });
        }

        flow.handleSummaryConfirm();
    };

    const handleSummaryClose = () => {
        if (amplitudeTrackingContext && !amplitudeSaveTrackedRef.current) {
            trackMultileadPreferencesClose({
                context: amplitudeTrackingContext,
                values: flow.summaryValues,
                autoMatchEnabled: flow.homeMatchBasicConsent,
                regionDetails: flow.regionDetails
            });
        }

        flow.handleSummaryClose();
    };

    if (!isOpen) {
        return null;
    }

    if (flow.step === "completed") {
        return null;
    }

    if (flow.step === "resolving" || flow.isLoading || flow.isHomeMatchConfigurationLoading) {
        return <HomeMatchBasicPreferencesFlowLoadingModal isOpen={isOpen} overlayVariant={overlayVariant} />;
    }

    if (flow.step === "survey") {
        const surveyCopy = getHomeMatchBasicSurveyCopy(flow.isEditingSurvey);

        return (
            <RegistrationPreferencesModal
                isOpen={isOpen}
                isPropertyApplication={Boolean(propertyId)}
                propertyId={propertyId}
                offerId={offerId || undefined}
                application={originalApplicationResponse}
                surveySource={flow.surveySource}
                title={surveyCopy.title}
                description={surveyCopy.description}
                submitButtonText={flow.isEditingSurvey ? "ZAPISZ" : "ZAPISZ SWOJE PREFERENCJE"}
                closeButtonText={surveyCopy.closeButtonText}
                onCancel={flow.isEditingSurvey ? flow.handleSurveyCancel : undefined}
                overrideDefaultValues={flow.isEditingSurvey ? flow.summaryValues : undefined}
                onSuccess={flow.handleSurveySuccess}
                showPriceHeader={!flow.isEditingSurvey}
                showCreditPlan={!flow.isEditingSurvey}
                layoutVariant={flow.isEditingSurvey ? "homeMatchBasicEdit" : "homeMatchBasicSurvey"}
                gtmEventCategory={flow.isEditingSurvey ? trackingContext?.eventCategory : undefined}
                gtmCustomLabel={flow.isEditingSurvey ? (trackingContext?.sourceModal ?? trackingContext?.eventName) : undefined}
                overlayVariant={overlayVariant}
            />
        );
    }

    if (flow.step === "resignation") {
        return (
            <HomeMatchBasicPreferencesFlowResignationModal
                isOpen={isOpen}
                isSaving={flow.isSaving}
                onDisable={() => flow.saveHomeMatchDecision(false, "stayModal")}
                onKeepEnabled={flow.handleKeepEnabled}
                onClose={flow.handleResignationClose}
                trackingContext={trackingContext}
                overlayVariant={overlayVariant}
            />
        );
    }

    if (flow.step === "phoneVerification") {
        return (
            <HomeMatchBasicPreferencesFlowPhoneVerificationModal
                isOpen={isOpen}
                onClose={flow.handlePhoneVerificationClose}
                onSuccess={flow.handlePhoneVerificationSuccess}
                onChangeNumber={flow.handlePhoneVerificationChangeNumber}
                overlayVariant={overlayVariant}
            />
        );
    }

    if (flow.step === "phoneUpdate") {
        return (
            <HomeMatchBasicPreferencesFlowPhoneUpdateModal
                isOpen={isOpen}
                onClose={flow.handlePhoneUpdateClose}
                onSuccess={flow.handlePhoneUpdateSuccess}
                overlayVariant={overlayVariant}
            />
        );
    }

    return (
        <HomeMatchBasicPreferencesFlowSummaryModal
            isOpen={isOpen}
            source={source}
            values={flow.summaryValues}
            regionDetails={flow.regionDetails}
            homeMatchBasicConsent={flow.homeMatchBasicConsent}
            isSaving={flow.isSaving}
            onHomeMatchBasicConsentChange={flow.setHomeMatchBasicConsent}
            onEditPreferences={handleEditPreferences}
            onConfirm={handleSummaryConfirm}
            onClose={handleSummaryClose}
            trackingContext={trackingContext}
            overlayVariant={overlayVariant}
        />
    );
};
